/**
 * How this works:
 * - EVENTS_HANDLERS defines which events are tracked and added to dataLayer
 * - Each event need to be `ds:` prefixed for GTM to trigger Event tag
 * - Triggered events are sent to GA4
 * - GA4 has custom dimensions and metrics to track app version and session duration
 * - Session is started when 'session:start' event is received and ended when 'session:end' event is received
 * 
 * To add new events:
 * - Add event to EVENT_HANDLERS
 * - If new event parameters were added, you need to
 *   1. Add GTM variable for each parameter
 *   2. Include those variables in Event tag to be passed GA4.
 * 
 * Testing:
 * - Preview GTM container (enables debugging). Use either localhost or ds.localtest.me.
 * - View events GA is receiving using GA's DebugView
 */

import Events from '~/events';
import * as D from '~/data-utils';
import { getVersionDescription } from './status';
import { initGTM } from './gtm-loader';
import { cookieJar, getApexDomain } from './cookie';

const HOUR = 3.6e+6;

let instance;

/**
 * @param {{ imdHash: string; }} context
 */
export function startAnalytics (context) {
  if (!instance) {
    instance = new GA4Tracker(context);
  }
}

/**
 * @typedef {{ event?: string; event_params: Record<string,string|number> }} GTMEventData
 */

/**
 * @template {AppEventKey} K
 * @callback EventHandler
 * @this {GA4Tracker}
 * @param {GTMEventData} data
 * @param {CustomEvent<AppEvent[K]>} event
 * @returns {void}
 */

/**
 * @type {(name: string, param_map: Record<string,string>) => EventHandler<any>}
 */
const detailHandler = (name, param_map) => (data, event) => {
  const dtl = event.detail;
  data.event = name;
  Object.entries(param_map).forEach(
    ([src,dst]) => data.event_params[dst] = src == '*' ? dtl : dtl[src]
  );
};

/** @typedef {CustomEvent<AppEvent[Extract<AppEventKey, '3rd-party:open'>]>} AE_3rdParty */

/** 
 * @template {AppEventKey} T
 * @type {{ [T: string]: EventHandler<T> }} */
const EVENT_HANDLERS = {
  'app:start': function (data) {
    data.event = 'ds:start_app';
    data.event_params.app_version = this.version;
  },

  'session:start': function (data) {
    data.event = 'ds:start_app_session';
    data.event_params.app_session = 1; // so that we can count sessions as custom metric
  },

  'session:end': function (data) {
    data.event = 'ds:end_app_session';
    data.event_params.session_duration_sec = this.session_duration_sec;
  },

  'language:change': function () {
    console.log(`Language changed to ${D.lang()}`);
  },

  'language:selection': detailHandler('ds:change_language', { 
    '*': 'language' 
  }),

  'search': detailHandler('ds:search', { 
    term: 'search_term' 
  }),

  'service:closest:show': detailHandler('ds:show_closest_service', { 
    name: 'service' 
  }),

  'service:highlight': detailHandler('ds:highlight_service', { 
    name: 'service' 
  }),

  'service:route': detailHandler('ds:route_to_service', { 
    name: 'service' 
  }),

  'poi': detailHandler('ds:poi', { 
    name: 'poi_name',
    action: 'poi_action'
  }),

  'view:change:after': detailHandler('ds:change_view', { 
    '*': 'view_name' 
  }),

  'ui:transform': (data, event) => {
    const detail = event.detail;
    data.event = 'ds:transform_ui';
    data.event_params.to_state = detail.to;
    data.event_params.from_state = detail.from;
  },

  '3rd-party:open': function (data, /** @type {AE_3rdParty} */ event) {
    const detail = event.detail;
    data.event = 'ds:open_3rd_party_content';
    data.event_params.content_url = detail.url;
    data.event_params.content_label = detail.label.map(x => Object.values(x).join()).join('|');
  }

};

class GA4Tracker {

  /**
   * @param {{ imdHash: string; }} context
   */
  constructor (context) {

    /**
     * @readonly
     * @type {Promise<any>}
     */
    this.gtm_loader = initGTM().then(() => {
      return new Promise((resolve, reject) => {
        let count = 100; // wait max 10 seconds
        const interval_id = /** @type {NodeJS.Timeout} */ (setInterval(() => {
          if (this.measurement_ids.length) {
            resolve();
            clearInterval(interval_id);
          }
          if (--count < 0) {
            reject();
            clearInterval(interval_id);
            console.error('No analytics will be collected because no measurement IDs!');
          }
        }, 100));
      });
    });

    /**
     * @private
     * @type {string}
     */
    this._apexDomain = getApexDomain(window);

    /** @type {string} */
    this.imdHash = context.imdHash;
    
    /** 
     * @private
     * @type {string} */
    this._version = undefined;

    /** 
     * @private
     * @type {number} */
    this._version_update = 0;
    
    /** 
     * @private
     * @type {'idle' | number} */
    this.session = 'idle';
    
    /** 
     * @private
     * @type {number} */
    this.session_start = Date.now();

    /** @type {Array} */
    this.data_layer = window['dataLayer'] = window['dataLayer'] || [];

    /** @type {string[]} */
    this._measurement_ids = [];
 
    Object.keys(EVENT_HANDLERS).forEach(
      t => Events.on(/** @type {AppEventKey} */ (t), this.emitDataLayerEvent.bind(this))
    );
  }

  get version () {
    const now = Date.now();
    if (!this._version || this._version_update + HOUR < now) {
      this._version_update = now;
      getVersionDescription(this).then(v => this._version = v);
    }
    return this._version;
  }

  get measurement_ids () {
    /** @type {{ tidr: { container: Record<string, unknown> }}} */
    const gtd = window['google_tag_data'];
    
    if (!gtd || this._measurement_ids.length) return this._measurement_ids;

    this._measurement_ids = Object.keys(gtd.tidr.container).reduce((acc, d) => {
      if (d.startsWith('G-')) acc.push(d.replace(/^G-/, ''));
      return acc;
    }, []);
    
    return this._measurement_ids;
  }

  get session_duration_sec () {
    return (Date.now() - this.session_start) / 1000;
  }

  /** @private */
  async endSession () {
    await this.updateSesssionID(); // prevent same session_id use on restart
    this.session = 'idle';
  }

  async updateSesssionID () {
    await this.gtm_loader;

    // const d = new Date();
    // d.setDate(d.getDate() + 30);
    // const expires = d.toUTCString();

    const domain = this._apexDomain;
    this.session = Date.now();
    // seems that it's enough to remove _ga cookie to get new session ID
    cookieJar.remove('_ga', domain); 
    
    // const cid = Math.floor(Math.random() * 1e10);
    // const ga_cookie = ga4.GA1(cid, this.session);
    // document.cookie = `${ga_cookie}; expires=${expires}; domain=${domain}; path=/`;

    this.measurement_ids.forEach(id => {
      
      const key = `_ga_${id}`;
      cookieJar.remove(key, domain); // <-- same here

      // const [, curr_value = ''] = document.cookie.match(new RegExp(`${key}=([^;]+)`)) || [];
      // const [, curr_sid = now] = curr_value.match(/^GS\d\.\d+\.(\d+)/) || [];
      // const legaxy_version = curr_value.startsWith('GS1');

      // const gs_cookie = legaxy_version
      //   ? ga4.GS1(id, this.session, 1, 1, this.session, cid)
      //   : ga4.GS2(id, this.session, 1, 1, this.session, cid);
      
      // document.cookie = `${gs_cookie}; expires=${expires}; domain=${domain}; path=/`;
    });
  }


  /** 
   * @param {CustomEvent} event 
   */
  async emitDataLayerEvent (event) {

    try {

      const type = /** @type {AppEventKey} */ (event.type);

      if (type == 'session:start') {
        await this.updateSesssionID();
        this.data_layer.push({ event: 'page_view' });
        this.session_start = Date.now();
      }
      else if (this.session == 'idle') {
        return;
      }

      /** @type {GTMEventData} */
      let data = { event_params: { language: D.lang() } };
      const handler = EVENT_HANDLERS[type];

      if (handler) {
        handler.call(this, data, event);
      }
      
      if (data.event) {
        this.data_layer.push(data);

        // LET Google Tag run before session ID get's changed
        if (type == 'session:end') {
          setTimeout(() => this.endSession(), 0);
        }
      }
      
    } catch (error) {
      console.error(error);
    }
    
  }

}





